import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { ref, get, set, query, orderByChild, equalTo } from 'firebase/database';
import { signOut } from 'firebase/auth';
import MobileLayout from '../../components/mobile/MobileLayout';
import PaymentGateway from '../../components/PaymentGateway';
import { apiFetch } from '../../utils/api';
import { usePlans } from '../../hooks/usePlans';

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */
interface UserData {
  name: string;
  email: string;
  phone: string;
  plan: string;
  onboardingCompleted: boolean;
  paymentStatus?: 'active' | 'failed' | 'pending' | 'pending_renewal' | 'cancelled' | 'expired' | 'trial';
  lastPaymentDate?: number;
  nextBillingDate?: number;
  stripeCustomerId?: string;
  stripePaymentMethodId?: string;
  failedPaymentAttempts?: number;
  cancelAtPeriodEnd?: boolean;
  subscriptionStatus?: string;
  billingCycle?: string;
  cardLast4?: string;
  cardBrand?: string;
  cardExpiry?: string;
  trialPlan?: string;
  trialStartedAt?: number;
  trialEndsAt?: number;
  trialUsed?: boolean;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  botId?: string;
  businessName?: string;
  description: string;
  amount: number;
  status: 'unpaid' | 'paid';
  type: 'setup_fee' | 'subscription' | 'subscription_renewal';
  contactEmail?: string;
  customerName?: string;
  customerEmail?: string;
  createdAt: number;
  dueDate?: number;
  paidAt?: number | null;
  transactionId?: string | null;
  paymentMethod?: string | null;
  plan?: string;
  date?: number;
}

/* ─────────────────────────────────────────────
   STYLE HELPERS
───────────────────────────────────────────── */
const cardStyle: React.CSSProperties = {
  borderRadius: 12,
  background: 'var(--bg-card)',
  border: '1px solid var(--card-border)',
  boxShadow: 'var(--card-shadow)',
  padding: 16,
  marginBottom: 16,
};

const sectionHeader: React.CSSProperties = {
  fontSize: 11,
  color: 'var(--text-faint)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 8,
  marginTop: 24,
  fontWeight: 700,
};

const primaryBtn: React.CSSProperties = {
  background: '#5B4FE8',
  color: 'white',
  borderRadius: 9,
  padding: '12px 20px',
  fontSize: 14,
  fontWeight: 600,
  border: 'none',
  width: '100%',
  cursor: 'pointer',
  minHeight: 44,
};

const inputStyle: React.CSSProperties = {
  background: 'var(--bg-input)',
  border: '1px solid var(--card-border)',
  borderRadius: 8,
  padding: '10px 14px',
  color: 'var(--text-primary)',
  fontSize: 14,
  width: '100%',
  boxSizing: 'border-box',
};

/* ─────────────────────────────────────────────
   STATUS BADGE
───────────────────────────────────────────── */
function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { bg: string; color: string; label: string }> = {
    active:          { bg: '#10b98122', color: '#10b981', label: 'Active' },
    trial:           { bg: '#a855f722', color: '#a855f7', label: 'Trial' },
    failed:          { bg: '#dc262622', color: '#f87171', label: 'Failed' },
    expired:         { bg: '#6b728022', color: '#9ca3af', label: 'Expired' },
    cancelled:       { bg: '#6b728022', color: '#9ca3af', label: 'Cancelled' },
    pending:         { bg: '#f59e0b22', color: '#fbbf24', label: 'Pending' },
    pending_renewal: { bg: '#f59e0b22', color: '#fbbf24', label: 'Renewal Due' },
  };
  const c = cfg[status] ?? { bg: '#6b728022', color: '#9ca3af', label: status };
  return (
    <span style={{ background: c.bg, color: c.color, fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>
      {c.label}
    </span>
  );
}

/* ─────────────────────────────────────────────
   INVOICE PAY MODAL (Mobile)
───────────────────────────────────────────── */
function InvoicePayModal({ invoice, user, userData, onSuccess, onClose }: {
  invoice: Invoice; user: any; userData: UserData | null; onSuccess: () => void; onClose: () => void;
}) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', padding: '24px 16px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 17 }}>Pay Invoice</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20, padding: 4, lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ background: 'var(--bg-page)', borderRadius: 10, padding: 14, marginBottom: 20, border: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Invoice #</span>
            <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>{invoice.invoiceNumber}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Service</span>
            <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500, maxWidth: 180, textAlign: 'right' }}>{invoice.description}</span>
          </div>
          {invoice.businessName && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Business</span>
              <span style={{ color: 'var(--text-primary)', fontSize: 13 }}>{invoice.businessName}</span>
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--bg-element)', marginTop: 10, paddingTop: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>Total</span>
            <span style={{ color: '#5B4FE8', fontWeight: 700, fontSize: 22 }}>${invoice.amount}</span>
          </div>
        </div>

        <PaymentGateway
          amount={invoice.amount}
          plan="setup"
          billingCycle="monthly"
          email={user?.email}
          phone={userData?.phone}
          userId={user?.uid}
          invoiceId={invoice.id}
          savedCardLast4={userData?.cardLast4}
          savedCardBrand={userData?.cardBrand}
          savedCardExpiry={userData?.cardExpiry}
          stripePaymentMethodId={userData?.stripePaymentMethodId}
          stripeCustomerId={userData?.stripeCustomerId}
          onSuccess={onSuccess}
          onError={(err: string) => alert('Payment failed: ' + err)}
        />

        <button onClick={onClose} style={{ width: '100%', marginTop: 12, padding: 12, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--card-border)', borderRadius: 9, cursor: 'pointer', fontSize: 14, minHeight: 44 }}>
          Cancel — pay later
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SUBSCRIPTION PAYMENT MODAL (Mobile)
───────────────────────────────────────────── */
function SubscriptionModal({ user, userData, selectedPlan, selectedPlanData, billingCycle, cardOnlyMode, onSuccess, onClose }: {
  user: any; userData: UserData | null; selectedPlan: string;
  selectedPlanData: { name: string; price: number } | undefined;
  billingCycle: 'monthly' | 'yearly'; cardOnlyMode: boolean;
  onSuccess: () => void; onClose: () => void;
}) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', padding: '24px 16px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 17 }}>
            {cardOnlyMode ? 'Save Payment Method' : 'Complete Payment'}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20, padding: 4, lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
          {cardOnlyMode
            ? 'Save your card for future upgrades — no charge today'
            : selectedPlanData ? `${selectedPlanData.name} Plan — $${selectedPlanData.price}/${billingCycle === 'monthly' ? 'month' : 'year'}` : ''}
        </div>

        {user && (
          <PaymentGateway
            amount={cardOnlyMode ? 0 : (selectedPlanData?.price ?? 0)}
            plan={cardOnlyMode ? 'free' : selectedPlan}
            billingCycle={billingCycle}
            email={user.email}
            phone={userData?.phone}
            userId={user.uid}
            savedCardLast4={userData?.cardLast4}
            savedCardBrand={userData?.cardBrand}
            savedCardExpiry={userData?.cardExpiry}
            stripePaymentMethodId={userData?.stripePaymentMethodId}
            stripeCustomerId={userData?.stripeCustomerId}
            onSuccess={onSuccess}
            onError={(err: string) => alert('Payment failed: ' + err)}
          />
        )}

        <button onClick={onClose} style={{ width: '100%', marginTop: 12, padding: 12, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--card-border)', borderRadius: 9, cursor: 'pointer', fontSize: 14, minHeight: 44 }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   TRIAL MODAL (Mobile)
───────────────────────────────────────────── */
function TrialCardElement() {
  const containerRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';
    const win = window as any;
    const stripe = win.Stripe?.(stripeKey);
    if (!stripe || !containerRef.current) return;
    const elements = stripe.elements();
    const card = elements.create('card', {
      style: {
        base: { color: 'var(--text-primary)', fontSize: '15px', fontFamily: 'system-ui, sans-serif', '::placeholder': { color: 'var(--text-faint)' }, backgroundColor: 'transparent' },
        invalid: { color: '#f87171' },
      },
    });
    card.mount(containerRef.current);
    win.__trialCardElement = card;
    return () => { card.destroy(); delete win.__trialCardElement; };
  }, []);
  return <div ref={containerRef} style={{ background: 'var(--bg-page)', border: '1px solid var(--card-border)', borderRadius: 10, padding: '14px 16px', minHeight: 46 }} />;
}

function TrialModal({ userData, user, onClose, onSuccess }: {
  userData: UserData | null; user: any; onClose: () => void; onSuccess: () => void;
}) {
  const [trialBillingCycle, setTrialBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [trialLoading, setTrialLoading] = useState(false);
  const [trialError, setTrialError] = useState('');
  const trialPreviewEndStr = new Date(Date.now() + 7 * 86400000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  const handleStartTrial = async () => {
    setTrialLoading(true);
    setTrialError('');
    try {
      const stripe = (window as any).Stripe?.(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');
      if (!stripe) throw new Error('Stripe not loaded');
      const res1 = await apiFetch('/api/trial/setup-intent', { method: 'POST' });
      const { clientSecret, error: siErr } = await res1.json();
      if (siErr) throw new Error(siErr);
      const cardEl = (window as any).__trialCardElement;
      if (!cardEl) throw new Error('Card element not ready');
      const { setupIntent, error: confirmErr } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card: cardEl, billing_details: { name: userData?.name || user?.displayName || '' } },
      });
      if (confirmErr) throw new Error(confirmErr.message);
      const res2 = await apiFetch('/api/trial/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentMethodId: setupIntent.payment_method, plan: 'business', billingCycle: trialBillingCycle, setupIntentId: setupIntent.id }),
      });
      const data = await res2.json();
      if (!data.success) throw new Error(data.error || 'Trial start failed');
      onSuccess();
    } catch (err: any) {
      setTrialError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setTrialLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1100 }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', padding: '24px 16px', width: '100%', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 17 }}>Start 7-Day Business Trial</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <div style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>Full Business plan access, free for 7 days. Card required — not charged until trial ends.</div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>BILLING CYCLE AFTER TRIAL</div>
          <div style={{ display: 'flex', background: 'var(--bg-page)', borderRadius: 10, padding: 4, gap: 4 }}>
            {(['monthly', 'yearly'] as const).map(cycle => (
              <button key={cycle} onClick={() => setTrialBillingCycle(cycle)} style={{ flex: 1, padding: 9, borderRadius: 7, border: 'none', background: trialBillingCycle === cycle ? '#5B4FE8' : 'transparent', color: trialBillingCycle === cycle ? 'white' : 'var(--text-muted)', fontWeight: 600, cursor: 'pointer', fontSize: 13, minHeight: 44 }}>
                {cycle === 'monthly' ? 'Monthly' : 'Yearly (save 20%)'}
              </button>
            ))}
          </div>
        </div>

        <div style={{ background: 'var(--bg-page)', borderRadius: 10, padding: 14, marginBottom: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid var(--card-border)' }}>
          <div>
            <div style={{ color: '#a78bfa', fontSize: 13, fontWeight: 600 }}>After trial ends</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
              {trialBillingCycle === 'monthly' ? '$45/month' : '$432/year (~$36/mo)'}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#10b981', fontWeight: 700, fontSize: 16 }}>Free</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 11 }}>for 7 days</div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, marginBottom: 8 }}>CARD DETAILS</div>
          <TrialCardElement />
        </div>

        <div style={{ fontSize: 11, color: 'var(--text-faint)', marginBottom: 16, lineHeight: 1.5 }}>
          By starting your trial you agree that your card will be charged {trialBillingCycle === 'monthly' ? '$45' : '$432'} on {trialPreviewEndStr} unless you cancel before then.
        </div>

        {trialError && (
          <div style={{ background: '#7f1d1d20', border: '1px solid #ef444440', borderRadius: 8, padding: '10px 14px', marginBottom: 12, color: '#fca5a5', fontSize: 13 }}>
            {trialError}
          </div>
        )}

        <button onClick={handleStartTrial} disabled={trialLoading} style={{ ...primaryBtn, background: trialLoading ? '#374151' : '#5B4FE8', cursor: trialLoading ? 'not-allowed' : 'pointer' }}>
          {trialLoading ? 'Starting trial...' : 'Start Free Trial'}
        </button>
        <button onClick={onClose} style={{ width: '100%', marginTop: 10, padding: 12, background: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--card-border)', borderRadius: 9, cursor: 'pointer', fontSize: 14, minHeight: 44 }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function BillingMobile() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { plans: dynamicPlans } = usePlans();

  const currentPlansForCycle = (cycle: 'monthly' | 'yearly') =>
    dynamicPlans.filter(p => !p.contactOnly).map(p => ({
      id: p.id, name: p.name, price: p[cycle] ?? 0,
      description: cycle === 'yearly' && p.id !== 'free' ? 'Save ~20%' : p.description,
      features: p.features, icon: p.icon, color: p.color, popular: p.popular,
    }));

  const [user, setUser] = useState<any>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [selectedPlan, setSelectedPlan] = useState('business');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [nextBillingDate, setNextBillingDate] = useState<Date | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<string>('active');
  const [failedAttempts, setFailedAttempts] = useState<number>(0);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [paynowVerifying, setPaynowVerifying] = useState(false);
  const [invoicesOpen, setInvoicesOpen] = useState(false);

  // Modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [cardOnlyMode, setCardOnlyMode] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [activeInvoice, setActiveInvoice] = useState<Invoice | null>(null);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [entForm, setEntForm] = useState({ name: '', email: '', company: '', size: '', message: '' });
  const [entSent, setEntSent] = useState(false);
  const [entLoading, setEntLoading] = useState(false);

  const invoiceIdFromUrl = searchParams.get('invoiceId');
  const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');

  const refreshUserData = useCallback(async () => {
    if (!user) return;
    const snap = await get(ref(db, `users/${user.uid}`));
    const data = snap.val() as UserData | null;
    setUserData(data);
    if (data?.plan) setSelectedPlan(data.plan);
    if (data?.paymentStatus) setPaymentStatus(data.paymentStatus);
    if (data?.failedPaymentAttempts) setFailedAttempts(data.failedPaymentAttempts);
    if (data?.lastPaymentDate) {
      const next = new Date(data.lastPaymentDate);
      next.setDate(next.getDate() + (data.billingCycle === 'yearly' ? 365 : 30));
      setNextBillingDate(next);
    }
  }, [user]);

  const loadInvoices = useCallback(async (uid: string) => {
    const snap = await get(ref(db, `invoices/${uid}`));
    if (snap.exists()) {
      const list: Invoice[] = [];
      snap.forEach((c) => { list.push({ id: c.key!, ...c.val() } as Invoice); });
      list.sort((a, b) => (b.createdAt || b.date || 0) - (a.createdAt || a.date || 0));
      setInvoices(list);
      return list;
    }
    setInvoices([]);
    return [] as Invoice[];
  }, []);

  // Paynow redirect return: auto-poll until confirmed
  useEffect(() => {
    if (!user) return;
    const pending = (() => { try { return JSON.parse(localStorage.getItem('paynow_pending') || 'null'); } catch { return null; } })();
    if (!pending?.pollUrl) return;
    setPaynowVerifying(true);
    const interval = setInterval(async () => {
      try {
        const res = await apiFetch(`/api/paynow/poll?pollUrl=${encodeURIComponent(pending.pollUrl)}&plan=${pending.plan}&billingCycle=${pending.billingCycle}&amount=${pending.amount}&reference=${pending.reference}`);
        const data = await res.json();
        if (data.paid) {
          clearInterval(interval);
          localStorage.removeItem('paynow_pending');
          setPaynowVerifying(false);
          await refreshUserData();
          await loadInvoices(user.uid);
          alert('Payment confirmed! Your plan has been upgraded.');
        }
      } catch {}
    }, 3000);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setPaynowVerifying(false);
      localStorage.removeItem('paynow_pending');
    }, 300000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [user, loadInvoices, refreshUserData]);

  // Auth + data load
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (cu) => {
      setUser(cu);
      if (!cu) { navigate('/login'); return; }
      const snap = await get(ref(db, `users/${cu.uid}`));
      const data = snap.val() as UserData | null;
      setUserData(data);
      if (data?.plan) setSelectedPlan(data.plan);
      if (data?.cancelAtPeriodEnd) setCancelAtPeriodEnd(true);
      if (data?.lastPaymentDate) {
        const last = new Date(data.lastPaymentDate);
        const next = new Date(last);
        next.setDate(last.getDate() + (data.billingCycle === 'yearly' ? 365 : 30));
        setNextBillingDate(next);
      }
      if (data?.paymentStatus) setPaymentStatus(data.paymentStatus);
      if (data?.failedPaymentAttempts) setFailedAttempts(data.failedPaymentAttempts);

      const allInvoices = await loadInvoices(cu.uid);
      if (invoiceIdFromUrl) {
        const target = allInvoices.find(inv => inv.id === invoiceIdFromUrl);
        if (target && target.status === 'unpaid') { setActiveInvoice(target); setShowInvoiceModal(true); }
      }
    });
    return unsub;
  }, [loadInvoices, invoiceIdFromUrl, navigate]);

  // Derived
  const currentPlans = currentPlansForCycle(billingCycle);
  const selectedPlanData = currentPlans.find(p => p.id === selectedPlan);
  const unpaidInvoices = invoices.filter(inv => inv.status === 'unpaid');
  const isTrialEligible = userData?.plan === 'free' && !userData?.trialUsed;
  const isOnTrial = userData?.paymentStatus === 'trial';
  const trialDaysLeft = userData?.trialEndsAt ? Math.max(0, Math.ceil((userData.trialEndsAt - Date.now()) / 86400000)) : 0;
  const trialEndStr = userData?.trialEndsAt ? new Date(userData.trialEndsAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  const getPlanLabel = () => {
    const m: Record<string, string> = { free: 'Free Plan', starter: 'Starter Plan', business: 'Business Plan', agency: 'Agency Plan' };
    return m[userData?.plan ?? 'free'] ?? 'Free Plan';
  };
  const getPlanPrice = () => {
    const m: Record<string, string> = { free: '$0/month', starter: '$15/month', business: '$45/month', agency: '$120/month' };
    return m[userData?.plan ?? 'free'] ?? '$0/month';
  };

  const handleSubscriptionPaymentSuccess = async () => {
    setShowSubscriptionModal(false);
    setCardOnlyMode(false);
    if (!user) return;
    const snap = await get(ref(db, `users/${user.uid}`));
    const data = snap.val() as UserData | null;
    setUserData(data);
    if (data?.plan) setSelectedPlan(data.plan);
    if (data?.paymentStatus) setPaymentStatus(data.paymentStatus);
    if (data?.lastPaymentDate) {
      const next = new Date(data.lastPaymentDate);
      next.setDate(next.getDate() + (data.billingCycle === 'yearly' ? 365 : 30));
      setNextBillingDate(next);
    }
    await loadInvoices(user.uid);
    alert(cardOnlyMode ? 'Card saved successfully!' : 'Payment successful! Your plan has been upgraded.');
  };

  const handleInvoicePaymentSuccess = async () => {
    if (!user || !activeInvoice) return;
    setShowInvoiceModal(false);
    const now = Date.now();
    await set(ref(db, `invoices/${user.uid}/${activeInvoice.id}/status`), 'paid');
    await set(ref(db, `invoices/${user.uid}/${activeInvoice.id}/paidAt`), now);
    if (activeInvoice.botId) {
      await set(ref(db, `bots/${activeInvoice.botId}/status`), 'active');
      await set(ref(db, `bots/${activeInvoice.botId}/setupFeePaid`), true);
      await set(ref(db, `bots/${activeInvoice.botId}/pendingInvoiceId`), null);
    }
    await loadInvoices(user.uid);
    setActiveInvoice(null);
    apiFetch('/api/notify/setup-fee-paid', { method: 'POST', body: JSON.stringify({ invoiceId: activeInvoice.id }) }).catch(() => {});
    navigate('/billing', { replace: true });
    alert('Payment successful! Your bot is now active.');
  };

  const generateInvoicePDF = async (invoice: Invoice) => {
    try {
      const res = await apiFetch(`/api/invoices/${invoice.id}/pdf`);
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoice.invoiceNumber || invoice.id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Could not download invoice. Please try again.');
    }
  };

  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel? You will keep access until the end of your billing period.')) return;
    const res = await apiFetch('/api/cancel-subscription', { method: 'POST', body: JSON.stringify({}) });
    const data = await res.json();
    if (data.success) { alert(`Subscription cancelled. Access ends ${new Date(data.endDate).toLocaleDateString()}`); window.location.reload(); }
    else alert('Failed to cancel: ' + data.error);
  };

  const handleReactivateSubscription = async () => {
    if (!window.confirm('Reactivate your subscription?')) return;
    const res = await apiFetch('/api/reactivate-subscription', { method: 'POST', body: JSON.stringify({}) });
    const data = await res.json();
    if (data.success) { alert('Subscription reactivated!'); window.location.reload(); }
    else alert('Failed to reactivate: ' + data.error);
  };

  /* ────────────────── RENDER ────────────────── */
  return (
    <MobileLayout title="Billing & Plans">
      <div style={{ padding: '0 16px' }}>

        {/* Paynow verifying banner */}
        {paynowVerifying && (
          <div style={{ marginTop: 16, padding: '12px 14px', background: '#a855f710', border: '1px solid #a855f740', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 18 }}>💳</span>
            <div>
              <div style={{ color: '#c084fc', fontWeight: 700, fontSize: 13 }}>Verifying Paynow payment...</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>Please wait while we confirm your payment.</div>
            </div>
          </div>
        )}

        {/* Overdue banner */}
        {unpaidInvoices.some(inv => inv.dueDate && inv.dueDate < Date.now()) && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: '#dc26260d', border: '1px solid #dc262633', borderLeft: '4px solid #dc2626', borderRadius: 12 }}>
            <div style={{ color: '#f87171', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Overdue Invoice</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 10 }}>You have an overdue invoice. Pay now to restore access.</div>
            <button
              onClick={() => { const inv = unpaidInvoices.find(i => i.dueDate && i.dueDate < Date.now()); if (inv) { setActiveInvoice(inv); setShowInvoiceModal(true); } }}
              style={{ ...primaryBtn, background: '#dc2626', padding: '10px 20px' }}
            >
              Pay Now
            </button>
          </div>
        )}

        {/* Pending renewal banner */}
        {paymentStatus === 'pending_renewal' && unpaidInvoices.length > 0 && (
          <div style={{ marginTop: 16, padding: '14px 16px', background: '#f59e0b0d', border: '1px solid #f59e0b40', borderLeft: '4px solid #f59e0b', borderRadius: 12 }}>
            <div style={{ color: '#fbbf24', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Renewal Invoice Outstanding</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 10 }}>Pay your renewal invoice to keep your plan active.</div>
            <button
              onClick={() => { const inv = unpaidInvoices.find(i => i.type === 'subscription_renewal'); if (inv) { setActiveInvoice(inv); setShowInvoiceModal(true); } }}
              style={{ ...primaryBtn, background: '#f59e0b', color: '#000', padding: '10px 20px' }}
            >
              Pay Renewal
            </button>
          </div>
        )}

        {/* ── Current plan banner ── */}
        <p style={sectionHeader}>Current Plan</p>
        <div style={{
          ...cardStyle,
          background: userData?.plan === 'free' || paymentStatus === 'failed'
            ? 'var(--bg-element)'
            : 'linear-gradient(135deg,#2d1b69,#1a1040)',
          border: '1px solid var(--card-border)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{getPlanLabel()}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{getPlanPrice()}</div>
            </div>
            <StatusBadge status={paymentStatus} />
          </div>

          {nextBillingDate && userData?.plan !== 'free' && paymentStatus === 'active' && (
            <div style={{ background: 'rgba(255,255,255,0.08)', borderRadius: 8, padding: '10px 14px', marginTop: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 2 }}>NEXT BILLING DATE</div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>
                {nextBillingDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          )}

          {isOnTrial && (
            <div style={{ marginTop: 12, background: 'rgba(124,58,237,0.25)', borderRadius: 8, padding: '10px 14px' }}>
              <div style={{ color: '#c084fc', fontWeight: 700, fontSize: 13 }}>Trial Active</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>
                {trialDaysLeft > 0 ? `${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''} remaining` : 'Trial ends today'}
                {trialEndStr ? ` · charged on ${trialEndStr}` : ''}
              </div>
            </div>
          )}

          {paymentStatus === 'failed' && (
            <div style={{ marginTop: 12, background: '#dc262622', border: '1px solid #dc262640', borderRadius: 8, padding: '10px 14px', color: '#f87171', fontSize: 13, fontWeight: 500 }}>
              Payment failed — update your payment method to restore access. {failedAttempts}/3 attempts.
            </div>
          )}
        </div>

        {/* Payment failed retry CTA */}
        {paymentStatus === 'failed' && (
          <button onClick={() => setShowSubscriptionModal(true)} style={{ ...primaryBtn, background: 'linear-gradient(135deg,#dc2626,#b91c1c)', marginBottom: 16 }}>
            Retry Failed Payment
          </button>
        )}

        {/* Expired / cancelled resubscribe */}
        {(paymentStatus === 'expired' || paymentStatus === 'cancelled') && userData?.plan === 'free' && (
          <div style={{ ...cardStyle, background: '#10b9810d', border: '1px solid #10b98130' }}>
            <div style={{ color: '#10b981', fontWeight: 700, fontSize: 14, marginBottom: 4 }}>Subscription Ended</div>
            <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 12 }}>Premium bots are suspended. Resubscribe to restore access.</div>
            <button onClick={() => setShowSubscriptionModal(true)} style={primaryBtn}>Resubscribe</button>
          </div>
        )}

        {/* ── Payment method ── */}
        <p style={sectionHeader}>Payment Method</p>
        <div style={cardStyle}>
          {userData?.stripePaymentMethodId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 32 }}>💳</div>
              <div style={{ flex: 1 }}>
                <div style={{ color: 'var(--text-primary)', fontWeight: 700, letterSpacing: 2, fontSize: 14 }}>•••• •••• •••• {userData.cardLast4 || '****'}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>{userData.cardBrand || 'Card'} · Expires {userData.cardExpiry || '**/**'}</div>
              </div>
              <span style={{ background: '#10b98122', color: '#10b981', fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>Active</span>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
              <div style={{ width: 44, height: 44, background: 'var(--bg-element)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>💳</div>
              <div>
                <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 14 }}>No payment method added</div>
                <div style={{ fontSize: 12, color: 'var(--text-faint)' }}>Required to upgrade your plan</div>
              </div>
            </div>
          )}
          <button onClick={() => { setCardOnlyMode(true); setShowSubscriptionModal(true); }} style={{ ...primaryBtn, background: 'var(--bg-element)', color: 'var(--text-primary)', border: '1px solid var(--card-border)' }}>
            {userData?.stripePaymentMethodId ? 'Update Card' : '+ Add Card'}
          </button>
        </div>

        {/* ── Billing cycle toggle ── */}
        <p style={sectionHeader}>Plans</p>
        <div style={{ display: 'flex', background: 'var(--bg-card)', border: '1px solid var(--card-border)', borderRadius: 40, padding: 4, marginBottom: 16 }}>
          {(['monthly', 'yearly'] as const).map(cycle => (
            <button key={cycle} onClick={() => setBillingCycle(cycle)} style={{ flex: 1, padding: '10px 0', borderRadius: 36, border: 'none', background: billingCycle === cycle ? '#5B4FE8' : 'transparent', color: billingCycle === cycle ? 'white' : 'var(--text-muted)', fontWeight: 600, cursor: 'pointer', fontSize: 14, minHeight: 44, position: 'relative' }}>
              {cycle.charAt(0).toUpperCase() + cycle.slice(1)}
              {cycle === 'yearly' && (
                <span style={{ position: 'absolute', top: -4, right: 4, background: '#10b981', color: 'white', fontSize: 9, padding: '2px 5px', borderRadius: 20, fontWeight: 700 }}>-20%</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Plan cards ── */}
        {currentPlans.map(plan => {
          const isCurrent = userData?.plan === plan.id && !isOnTrial;
          const isSelected = selectedPlan === plan.id;
          const isTrialActive = isOnTrial && plan.id === 'business';
          return (
            <div
              key={plan.id}
              onClick={() => !isCurrent && !isOnTrial && setSelectedPlan(plan.id)}
              style={{ ...cardStyle, border: isSelected ? `2px solid ${plan.color}` : '1px solid var(--card-border)', cursor: isCurrent || isOnTrial ? 'default' : 'pointer', position: 'relative', overflow: 'hidden' }}
            >
              {/* Badges */}
              <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
                {isCurrent && <span style={{ background: '#10b98122', color: '#10b981', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>CURRENT</span>}
                {isTrialActive && <span style={{ background: '#7c3aed22', color: '#a78bfa', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>TRIAL ACTIVE</span>}
                {plan.popular && !isCurrent && !isTrialActive && <span style={{ background: plan.color + '22', color: plan.color, fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>POPULAR</span>}
                {isTrialEligible && plan.id === 'business' && <span style={{ background: '#5B4FE822', color: '#818cf8', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>7-DAY TRIAL</span>}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 28, marginBottom: 4 }}>{plan.icon}</div>
                  <div style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700 }}>{plan.name}</div>
                  <div style={{ color: 'var(--text-faint)', fontSize: 12, marginTop: 2 }}>{plan.description}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ color: 'var(--text-primary)', fontSize: 26, fontWeight: 700 }}>${plan.price}</span>
                  <span style={{ color: 'var(--text-faint)', fontSize: 12 }}>/{billingCycle === 'monthly' ? 'mo' : 'yr'}</span>
                </div>
              </div>

              {/* Features */}
              <div style={{ borderTop: '1px solid var(--bg-element)', paddingTop: 12, marginBottom: 12 }}>
                {plan.features.slice(0, 4).map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, fontSize: 12, color: 'var(--text-secondary)' }}>
                    <span style={{ color: plan.color, fontWeight: 700 }}>✓</span> {f}
                  </div>
                ))}
                {plan.features.length > 4 && (
                  <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 4 }}>+{plan.features.length - 4} more features</div>
                )}
              </div>

              {/* Action buttons */}
              <button
                disabled={isCurrent || isOnTrial}
                onClick={(e) => { e.stopPropagation(); if (!isCurrent && !isOnTrial) { setSelectedPlan(plan.id); if (plan.id !== 'free') setShowSubscriptionModal(true); } }}
                style={{ width: '100%', padding: '11px', background: isCurrent || isTrialActive ? 'var(--bg-element)' : isSelected ? plan.color : 'var(--bg-element)', color: isCurrent || isTrialActive ? 'var(--text-muted)' : isSelected ? 'white' : 'var(--text-muted)', border: 'none', borderRadius: 9, fontWeight: 600, cursor: isCurrent || isOnTrial ? 'not-allowed' : 'pointer', fontSize: 14, minHeight: 44 }}
              >
                {isTrialActive ? 'On Trial' : isCurrent ? 'Current Plan' : isSelected && plan.id !== 'free' ? `Upgrade to ${plan.name}` : 'Select'}
              </button>

              {isTrialEligible && plan.id === 'business' && (
                <button
                  onClick={(e) => { e.stopPropagation(); setShowTrialModal(true); }}
                  style={{ width: '100%', marginTop: 8, padding: '11px', background: 'transparent', color: '#a78bfa', border: '1px solid #5B4FE8', borderRadius: 9, fontWeight: 600, cursor: 'pointer', fontSize: 14, minHeight: 44 }}
                >
                  Start 7-Day Free Trial
                </button>
              )}
            </div>
          );
        })}

        {/* Enterprise card */}
        {(() => {
          const ent = dynamicPlans.find(p => p.id === 'enterprise');
          if (!ent) return null;
          return (
            <div onClick={() => setShowEnterpriseModal(true)} style={{ ...cardStyle, background: 'linear-gradient(135deg,#1a1409,#211a05)', border: '1px solid #f59e0b40', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 28, marginBottom: 4 }}>{ent.icon}</div>
                  <div style={{ color: 'var(--text-primary)', fontSize: 18, fontWeight: 700 }}>{ent.name}</div>
                  <div style={{ color: 'var(--text-faint)', fontSize: 12 }}>{ent.description}</div>
                </div>
                <span style={{ background: '#f59e0b22', color: '#fbbf24', fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 20 }}>CUSTOM</span>
              </div>
              <div style={{ color: '#fbbf24', fontSize: 18, fontWeight: 700, marginBottom: 12 }}>Custom pricing</div>
              <div style={{ borderTop: '1px solid #f59e0b20', paddingTop: 12, marginBottom: 12 }}>
                {ent.features.slice(0, 4).map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, fontSize: 12, color: 'var(--text-secondary)' }}>
                    <span style={{ color: '#f59e0b' }}>✓</span> {f}
                  </div>
                ))}
              </div>
              <button style={{ width: '100%', padding: 11, background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#000', border: 'none', borderRadius: 9, fontWeight: 700, cursor: 'pointer', fontSize: 14, minHeight: 44 }}>
                Contact Sales
              </button>
            </div>
          );
        })()}

        {/* Upgrade CTA if plan changed */}
        {userData?.plan !== selectedPlan && selectedPlan !== 'free' && !isOnTrial && (
          <button onClick={() => setShowSubscriptionModal(true)} style={{ ...primaryBtn, marginBottom: 16, background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)' }}>
            Upgrade to {selectedPlanData?.name} — ${selectedPlanData?.price}/{billingCycle === 'monthly' ? 'mo' : 'yr'}
          </button>
        )}

        {/* Cancel / reactivate */}
        {userData?.plan !== 'free' && paymentStatus === 'active' && !cancelAtPeriodEnd && (
          <button onClick={handleCancelSubscription} style={{ width: '100%', padding: '11px', background: 'transparent', color: 'var(--text-faint)', border: '1px solid var(--card-border)', borderRadius: 9, cursor: 'pointer', fontSize: 13, minHeight: 44, marginBottom: 16 }}>
            Cancel Subscription
          </button>
        )}
        {cancelAtPeriodEnd && (
          <button onClick={handleReactivateSubscription} style={{ ...primaryBtn, background: 'linear-gradient(135deg,#5B4FE8,#7c3aed)', marginBottom: 16 }}>
            Reactivate Subscription
          </button>
        )}

        {/* ── Invoice history ── */}
        <p style={sectionHeader}>Billing History</p>
        <div style={cardStyle}>
          <button
            onClick={() => setInvoicesOpen(!invoicesOpen)}
            style={{ width: '100%', background: 'none', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: 0, cursor: 'pointer', minHeight: 44 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>Invoices</span>
              {unpaidInvoices.length > 0 && (
                <span style={{ background: '#f59e0b22', color: '#fbbf24', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>{unpaidInvoices.length} unpaid</span>
              )}
            </div>
            <span style={{ color: 'var(--text-muted)', fontSize: 18 }}>{invoicesOpen ? '▲' : '▼'}</span>
          </button>

          {invoicesOpen && (
            <div style={{ marginTop: 14, borderTop: '1px solid var(--bg-element)', paddingTop: 14 }}>
              {invoices.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-faint)', fontSize: 13 }}>
                  No invoices yet. Your first invoice appears here after your first payment.
                </div>
              ) : (
                invoices.map(inv => {
                  const overdue = inv.status === 'unpaid' && inv.dueDate && inv.dueDate < Date.now();
                  return (
                    <div key={inv.id} style={{ padding: '14px 0', borderBottom: '1px solid var(--bg-element)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 13 }}>{inv.invoiceNumber}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>{inv.description}</div>
                          {inv.businessName && <div style={{ color: 'var(--text-faint)', fontSize: 11, marginTop: 1 }}>{inv.businessName}</div>}
                          <div style={{ color: 'var(--text-faint)', fontSize: 11, marginTop: 2 }}>
                            {new Date(inv.createdAt || inv.date || Date.now()).toLocaleDateString()}
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', marginLeft: 12 }}>
                          <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 15 }}>${inv.amount}</div>
                          <div style={{ marginTop: 4 }}>
                            {inv.status === 'paid' ? (
                              <span style={{ background: '#10b98122', color: '#10b981', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>Paid</span>
                            ) : (
                              <span style={{ background: overdue ? '#dc262622' : '#f59e0b22', color: overdue ? '#f87171' : '#fbbf24', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20 }}>
                                {overdue ? 'Overdue' : 'Unpaid'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                        {inv.status === 'unpaid' && (
                          <button
                            onClick={() => { setActiveInvoice(inv); setShowInvoiceModal(true); }}
                            style={{ flex: 1, padding: '9px', background: overdue ? '#dc2626' : '#5B4FE8', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13, minHeight: 40 }}
                          >
                            Pay ${inv.amount}
                          </button>
                        )}
                        <button
                          onClick={() => generateInvoicePDF(inv)}
                          style={{ flex: inv.status === 'paid' ? 1 : 0, padding: '9px 14px', background: 'var(--bg-element)', color: 'var(--text-secondary)', border: '1px solid var(--card-border)', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: 13, minHeight: 40, whiteSpace: 'nowrap' }}
                        >
                          PDF
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        <div style={{ height: 24 }} />
      </div>

      {/* ── Subscription modal ── */}
      {showSubscriptionModal && user && selectedPlanData && (
        <SubscriptionModal
          user={user}
          userData={userData}
          selectedPlan={selectedPlan}
          selectedPlanData={selectedPlanData}
          billingCycle={billingCycle}
          cardOnlyMode={cardOnlyMode}
          onSuccess={handleSubscriptionPaymentSuccess}
          onClose={() => { setShowSubscriptionModal(false); setCardOnlyMode(false); }}
        />
      )}

      {/* ── Invoice modal ── */}
      {showInvoiceModal && activeInvoice && user && (
        <InvoicePayModal
          invoice={activeInvoice}
          user={user}
          userData={userData}
          onSuccess={handleInvoicePaymentSuccess}
          onClose={() => { setShowInvoiceModal(false); navigate('/billing', { replace: true }); }}
        />
      )}

      {/* ── Trial modal ── */}
      {showTrialModal && user && (
        <TrialModal
          userData={userData}
          user={user}
          onClose={() => setShowTrialModal(false)}
          onSuccess={async () => {
            setShowTrialModal(false);
            await refreshUserData();
            alert('Your 7-day Business trial has started! Enjoy full access.');
          }}
        />
      )}

      {/* ── Enterprise modal ── */}
      {showEnterpriseModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', padding: '24px 16px', width: '100%', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #f59e0b30' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ color: '#fbbf24', fontSize: 18, fontWeight: 700 }}>Enterprise Plan</div>
              <button onClick={() => { setShowEnterpriseModal(false); setEntSent(false); }} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
            </div>
            <div style={{ color: 'var(--text-faint)', fontSize: 13, marginBottom: 20 }}>Tell us about your needs — we'll respond within 1 business day.</div>

            {entSent ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                <div style={{ color: 'var(--text-primary)', fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Enquiry sent!</div>
                <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Our sales team will contact you at <strong>{entForm.email}</strong> within 1 business day.</div>
              </div>
            ) : (
              <>
                {[
                  { label: 'Your Name *', key: 'name', type: 'text', placeholder: 'Jane Doe' },
                  { label: 'Email *', key: 'email', type: 'email', placeholder: 'jane@company.com' },
                  { label: 'Company', key: 'company', type: 'text', placeholder: 'Acme Ltd' },
                  { label: 'Team Size', key: 'size', type: 'text', placeholder: 'e.g. 20–50 people' },
                ].map(({ label, key, type, placeholder }) => (
                  <div key={key} style={{ marginBottom: 14 }}>
                    <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{label}</label>
                    <input type={type} value={(entForm as any)[key]} onChange={e => setEntForm(f => ({ ...f, [key]: e.target.value }))} placeholder={placeholder} style={inputStyle} />
                  </div>
                ))}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>What do you need?</label>
                  <textarea value={entForm.message} onChange={e => setEntForm(f => ({ ...f, message: e.target.value }))} rows={3} placeholder="Describe your use case…" style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }} />
                </div>
                <button
                  disabled={entLoading || !entForm.name || !entForm.email}
                  onClick={async () => {
                    setEntLoading(true);
                    try {
                      await fetch(`${API_BASE}/api/enterprise-enquiry`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(entForm) });
                      setEntSent(true);
                    } finally { setEntLoading(false); }
                  }}
                  style={{ ...primaryBtn, background: entLoading || !entForm.name || !entForm.email ? 'var(--bg-element)' : 'linear-gradient(135deg,#f59e0b,#d97706)', color: entLoading || !entForm.name || !entForm.email ? 'var(--text-muted)' : '#000', cursor: entLoading || !entForm.name || !entForm.email ? 'not-allowed' : 'pointer' }}
                >
                  {entLoading ? 'Sending…' : 'Send Enquiry'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </MobileLayout>
  );
}
